import { Color } from 'three';

export const bgBlack = '#171717';
export const offblack = '#2A2A2A';
export const legendaryCard = '#ADB9DA';
export const standardCardWhite = '#E5E5EA';
export const legendaryWhite = '#EEEEEE';

/**
 * Get the vividness of a lightness value.
 * The vividness is the distance of the lightness value from its median (50) on a scale of 1-100.
 * A color with a lightness of 50 is 'most vivid' (100). 0 or 100 'least vivid' (0).
 *
 * @param {Number} lightness - a lightness value from 0 - 100.
 * @return {Number} the vividness value from 0 - 100.
 */
function getVividness(lightness: number): number {
    return 100 - Math.abs(50 - lightness) * 2;
}

interface RGB {
    r: number;
    g: number;
    b: number;
}

interface HSL {
    h: number;
    s: number;
    l: number;
}

/**
 * Get the perceptual luminance amount of an rgb value.
 *
 * rgb = an Object containing r, g, b numbers
 * hdtv = bool, whether to use Rec.709(HDTV) coefficients or Rec.601(PAL/NTSC)
 */
function getRgbLuminance(rgb: RGB, hdtv: boolean = false): number {
    if (hdtv) {
        return rgb.r * 0.2126 + rgb.g * 0.7152 + rgb.b * 0.0722;
    } else {
        return rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114;
    }
}

/**
 * Get a split compliment of a color within a configurable range of the direct compliment.
 *
 * @param {Number} hue - the hue of a color (0 - 360) to find a split compliment hue of.
 * @param {Number} range - the range in degrees around the direct compliment that a split will be chosen from at random.
 * @param {Boolean} hardSplit - whether to choose a color from the extents of the range (true) or anywhere within (false).
 * @return {Number} - the split compliment as a hue (0 - 360).
 */
function getSplitCompliment(hue: number, range: number = 30, hardSplit: boolean = false): number {
    let split = -(range / 2);

    if (hardSplit) {
        if (Math.random() > 0.5) {
            split += range;
        }
    } else {
        split += Math.random() * range;
    }

    return wrapHue(hue + 180 + split);
}

/**
 * Wrap a hue to within a range of 0 - 360.
 *
 * @param {Number} hue - a hue in degrees at 0-360 scale.
 * @return {Number} the hue wrapped within 0-360.
 */
function wrapHue(hue: number): number {
    return ((hue % 360) + 360) % 360;
}

/**
 */
function isBetween(value: number, lowerExtent: number, upperExtent: number): boolean {
    return value >= lowerExtent && value < upperExtent;
}

/**
 */
function areAnalogous(hueA: number, hueB: number): boolean {
    return isBetween(hueDistance(hueA, hueB), 0, 30);
}

/**
 */
function areTetradic(hueA: number, hueB: number): boolean {
    return isBetween(hueDistance(hueA, hueB), 60, 105);
}

/**
 */
function areTriadic(hueA: number, hueB: number): boolean {
    return isBetween(hueDistance(hueA, hueB), 105, 135);
}

/**
 */
function areSplitCompliment(hueA: number, hueB: number): boolean {
    return isBetween(hueDistance(hueA, hueB), 150, 180);
}

/**
 */
function hueDistance(hueA: number, hueB: number): number {
    let d = Math.abs(hueA - hueB);
    if (d > 180) {
        d = Math.abs(d - 360);
    }
    return d;
}

/**
 */
function rgbToHsl(rgb: RGB, useInt: boolean = false): HSL {
    let r = rgb.r;
    let g = rgb.g;
    let b = rgb.b;

    if (useInt) {
        r *= 255;
        g *= 255;
        b *= 255;
    }

    const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    let h = (max + min) / 2;
    let s = (max + min) / 2;
    let l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;

        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }

        h /= 6;
    }

    h *= 360;
    s *= 100;
    l *= 100;

    return { h, s, l };
}

/**
 */
function hslToRgb(hsl: HSL, useInt: boolean = false): RGB {
    const h = hsl.h / 360;
    const s = hsl.s / 100;
    const l = hsl.l / 100;

    let r, g, b;

    function hue2rgb(p: number, q: number, t: number): number {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    }

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    if (useInt) {
        r *= 255;
        g *= 255;
        b *= 255;
    }

    return { r, g, b };
}

/**
 * Return an attenuated hsl
 * Eliminates areas of too high of vibrance, subjective to aesthetic.
 */
function attenuateVibrance(hsl: HSL): HSL {
    if (hsl.h < 10 || hsl.h > 170) {
        // reduce the saturation of very hot reds

        hsl.s = 92;
    }

    return hsl;
}

function hslToHex(hsl: HSL): number {
    const { r, g, b } = hslToRgb(hsl, true);
    return (1 << 24) + (r << 16) + (g << 8) + b;
}

/**
 * Get the background color to use with an Onlybots colorway.
 * Bespoke logic to get a color witin the Onlybots aesthetic.
 *
 * @param {Color} bodyColor
 * @param {Color} eyeColor
 * @return {[HSL, number]} the base background color to use '#ffffff`
 */
function getBackgroundColorFromBot(bodyColor: Color, eyeColor: Color): [HSL, number] {
    // use a THREE.Color to convert from linear to SRGB (@TODO replace with implementation not reliant on THREE).

    const colorA = bodyColor;
    const colorB = eyeColor;
    colorA.convertSRGBToLinear();
    colorB.convertSRGBToLinear();

    // the colors as rgb (0.0-1.0) and hsl (0-360,0-100,0-100) Objects

    const rgbColorA = { r: colorA.r, g: colorA.g, b: colorA.b };
    const rgbColorB = { r: colorB.r, g: colorB.g, b: colorB.b };

    const hslColorA = rgbToHsl(rgbColorA);
    const hslColorB = rgbToHsl(rgbColorB);

    const hueA = hslColorA.h;
    const hueB = hslColorB.h;

    // weighting consts
    // adjust priority of different color factors as input to the final color selection

    const saturationWeight = 1.6;
    const vividnessWeight = 1.0;
    const luminanceWeight = 0.76;

    // Generate a score for each color based on the weighted sums of their saturation, vividness, and luminance.
    // The color that is the most strong across all three factors should 'win'.
    // Pretty naive approach but it seems to work nicely (o__O).

    const scoreColorA =
        hslColorA.s * saturationWeight +
        getVividness(hslColorA.l) * vividnessWeight +
        getRgbLuminance(rgbColorA) * 100 * luminanceWeight;

    const scoreColorB =
        hslColorB.s * saturationWeight +
        getVividness(hslColorB.l) * vividnessWeight +
        getRgbLuminance(rgbColorB) * 100 * luminanceWeight;

    // reference the 'winning' selected color's hsl

    const selectedHslColor = scoreColorA > scoreColorB ? hslColorA : hslColorB;

    // initialize the color to return with the winning color

    const bgHslColor = { h: selectedHslColor.h, s: selectedHslColor.s, l: selectedHslColor.l };

    // set the background color based on the relationship of the colors

    // console.log("HUE DELTA: " + hueDistance( hueA, hueB ) );

    if (areAnalogous(hueA, hueB)) {
        // console.log("--ANALOGOUS--");

        // choose a (slightly random) split compliment to the richer of the 2 colors

        // selectedColor = (richer( hslColorA, hslColorB )) ? hslColorA : hslColorB;
        // selectedColor.h = wrap( 0, 360, selectedColor.h - (180 - 15 + Math.random() * 30) );

        bgHslColor.h = getSplitCompliment(bgHslColor.h);
    } else if (areTetradic(hueA, hueB)) {
        // console.log("--TETRADIC--");

        // choose a (slightly random) split compliment to the higher scoring of the 2 colors

        // selectedColor.h = wrap( 0, 360, selectedColor.h - (180 - 15 + Math.random() * 30) );

        bgHslColor.h = getSplitCompliment(bgHslColor.h);
    } else if (areTriadic(hueA, hueB)) {
        // console.log("--TRIADIC--");

        // choose the third missing triadic color

        // rotate 120 degrees from the selected color

        bgHslColor.h = wrapHue(bgHslColor.h - 120);

        // if that isn't far enough apart from either color, rotate again

        if (hueDistance(bgHslColor.h, hslColorA.h) < 40 || hueDistance(bgHslColor.h, hslColorB.h) < 40) {
            bgHslColor.h = wrapHue(bgHslColor.h - 120);
        }

        // adjust certain harsh outcomes manually...

        if (Math.round(bgHslColor.h) === 119) {
            bgHslColor.h = wrapHue(bgHslColor.h + 80);
        }
    } else if (areSplitCompliment(hueA, hueB)) {
        // console.log("--SPLITCOMPLIMENT--");

        bgHslColor.h = hslColorA.h;
        bgHslColor.s = hslColorA.s;
        bgHslColor.l = hslColorA.l;

        bgHslColor.h = getSplitCompliment(bgHslColor.h);
    } else {
        // console.log("--OTHER--");

        bgHslColor.h = getSplitCompliment(bgHslColor.h);
    }

    // Adjust the brightness and saturation of the chosen color to desired range:

    const bodyColorLuminance = getRgbLuminance(rgbColorA);

    if (bodyColorLuminance < 0.2) {
        bgHslColor.s *= 0.95;
        bgHslColor.l = 55;
    } else {
        bgHslColor.s *= 0.95;
        // bgHslColor.l = 10;
    }

    //

    attenuateVibrance(bgHslColor);

    // convert the selected color back to hex and return the hex value

    const bgRgbColor = hslToRgb(bgHslColor, true);
    const bgHexColor = (1 << 24) + (bgRgbColor.r << 16) + (bgRgbColor.g << 8) + bgRgbColor.b;

    return [bgHslColor, bgHexColor];
}

function hslToHexString(hsl: HSL): string {
    const color = new Color(hslToHex(hsl));
    return `#${color.getHexString()}`;
}

export function getGradientColorsFromBot(
    bodyColor: Color,
    eyeColor: Color,
    legendary: boolean = false
): [[string, string], [string, string]] {
    const [hslColor] = getBackgroundColorFromBot(bodyColor, eyeColor);

    const insideGradientTop = hslToHexString(hslColor);

    const insideGradientHSL = { ...hslColor }; // clone color so we can modify
    if (legendary) {
        insideGradientHSL.l *= 0.4;
    } else {
        insideGradientHSL.l *= 1.9;
        if (insideGradientHSL.l > 100) {
            insideGradientHSL.l = 100;
        } else if (insideGradientHSL.l < 80) {
            insideGradientHSL.l = 80;
        }
    }
    const insideGradientBottom = hslToHexString(insideGradientHSL);

    const outsideGradientTopHSL = { ...hslColor };
    outsideGradientTopHSL.l = 80;
    outsideGradientTopHSL.s *= 0.4;
    const outsideGradientTop = hslToHexString(outsideGradientTopHSL);

    const outsideGradientBottomHSL = { ...hslColor };
    outsideGradientBottomHSL.l = 50;
    outsideGradientBottomHSL.s *= 0.2;
    const outsideGradientBottom = hslToHexString(outsideGradientBottomHSL);

    return [
        [outsideGradientTop, outsideGradientBottom],
        [insideGradientTop, insideGradientBottom],
    ];
}
